import { useCallback } from 'react'
import { useIsUnmounted, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { useEditingStateWithCtx } from '@/atoms'
import { updateMobileAppInteraction } from '@/features/domain/territory'
import { DriverAssistanceFormValues } from '../../../types'

export function useOnSubmit() {
  const isUnmounted = useIsUnmounted()
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const { resetEditing } = useEditingStateWithCtx('setup')

  const updateDriverAssistance = useCallback(
    async (driverAssistanceValues: uui.domain.client.rm.MobileAppInteraction) => {
      try {
        const result = await dispatch(updateMobileAppInteraction(driverAssistanceValues))

        if (updateMobileAppInteraction.rejected.match(result)) {
          throw new Error(result.payload?.message ?? 'Internal error')
        }
        if (isUnmounted()) return

        // if the update has been completed successfully let's close the form
        resetEditing()
      } catch (error) {
        toast.error(error.message)
        if (isUnmounted()) return
      }
    },
    [dispatch, isUnmounted, resetEditing, toast],
  )

  return useCallback(
    async (formValues: DriverAssistanceFormValues) => {
      const {
        type,
        includeNote,
        includeAudio,
        includePicture,
        includeSignature,
        checkOut,
        checkIn,
      } = formValues

      switch (type) {
        case 'NONE':
          await updateDriverAssistance({ type })
          break

        case 'ASSISTED_TIME_OUT':
          await updateDriverAssistance({
            type: checkIn ? 'ASSISTED_TIME_IN_OUT' : 'ASSISTED_TIME_OUT',
          })
          break

        default:
          await updateDriverAssistance({
            type,
            driverRequiredFlow: {
              includeNote,
              includeAudio,
              includePicture,
              includeSignature,
              mandatoryDeparture: checkOut,
            },
          })
      }
    },
    [updateDriverAssistance],
  )
}
